<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>
        
        <!-- Create Rfx Modal -->
        <b-modal
            v-model="modalShowing"
            title="New Sourcing Event"
            @show="onModalShow"
            @hidden="onModalHidden"
            centered
            size="lg"
            :hide-footer="showSuccess"
            content-class="bg-gray-50"
        >
            <div class="d-flex" style="min-height: 250px">
                <transition name="fade-fast" mode="out-in">
                    <save-success v-if="showSuccess" key="success" :message="successMessage"></save-success>
                    <div v-else key="form" class="w-100 spl-inline-form px-4 py-3">
                        <b-overlay :show="loading">
                                
                            <hot-edit-input
                                label="Event Title"
                                label-align="left"
                                v-model="itemData.title"
                                edit-mode
                                required
                                ref="title"
                            ></hot-edit-input>

                            <hr>

                            <hot-edit-select
                                label="Event Type"
                                label-align="left"
                                v-model="itemData.rfx_type"
                                :options="setupData.rfx_type_options"
                                edit-mode
                                required
                                :loading="loading || loadingSetupData"
                                input-class="w-auto"
                                ref="rfx_type"
                            ></hot-edit-select>

                            <hr>

                            <hot-edit-select
                                label="Sourcing Category"
                                label-align="left"
                                v-model="itemData.sourcing_category"
                                :options="setupData.sourcing_category_options"
                                edit-mode
                                :loading="loading || loadingSetupData"
                                input-class="w-auto"
                                ref="sourcing_category"
                            ></hot-edit-select>

                            <div v-if="!allRefsValid" class="py-3 px-4">
                                <h6 class="text-danger">Please fix these errors:</h6>
                                <p 
                                    v-for="message in refsInvalidList" 
                                    :key="message" 
                                    class="text-danger mb-1 small"
                                >
                                    {{ message }}
                                </p>
                            </div>

                        </b-overlay>
                    </div>
                </transition>
            </div>
            <template v-slot:modal-footer="{ cancel }">
                <b-button variant="secondary" @click="cancel()">Cancel</b-button>
                <b-button variant="primary" @click="saveNewItem">Create &amp; continue setup &rarr;</b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
// THIS IS COMBINED VERSION OF FORM AND MODAL... since this will only be used as a modal, and not used for updates (only create)

import { suppliosApi } from '../js/api-service';
import SaveSuccess from './functional-comps/SaveSuccess';
import { 
    HotEditInput, 
    HotEditSelect, 
    //HotEditTextarea, 
    //HotEditSwitch,
    //HotEditMoney,
    //HotEditDatepicker
} from '../js/custom-form-components';
import isEmpty from 'lodash/isEmpty';
//import intersection from 'lodash/intersection';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';

export default {
    name: 'RfxCreateModal',
    components: {
        SaveSuccess,
        HotEditInput,
        HotEditSelect,
    },
    props: {
        newData: {  // if you want to FORCE any field values for CREATE NEW objects (not update existing), pass it in here
            // fields must be in same format/names as used in API
            type: Object,
            required: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error while getting data from server.',
            error: false,
            modalShowing: false, // controls modal show/hide -- set via methods
            loading: false,
            showSuccess: false,  // boolean to control showing success message
            successMessage: "Sourcing Event created.  Let's setup the details...",
            successMessageTime: 2000,
            itemData: {
                // we do Object.assign() from blankItemData (and newData) when modal shows
            },
            blankItemData: {  // have to make sure this matches with field inputs (v-models) and formatDataForSave
                title: '',
                rfx_type: '',
                sourcing_category: '',
            },
            loadingSetupData: false,  
            setupData: {  // these will get set / overriden during this.getSetupData()
                rfx_type_options: [],
                sourcing_category_options: [],
            },
            allRefsValid: true,  // set true initially... then false if any invalid found when checking, this is used as flag to show refsInvalid
            refsInvalidList: [],  // set this in checkRefsValid()
        }
    },
    computed: {

    },
    methods: {
        // METHODS TO CALL FROM PARENT
        showModal: function() { // main public method to use
            // call this from parent using $refs -- it triggers everything else
            this.modalShowing = true;
            // all the typical 'mounted' stuff gets called in modal show hook, onModalShow
        },
        hideModal: function() {  // only useful if you want to hide this programmatically from parent
            this.modalShowing = false;
        },
        // ============== internal stuff ======
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            if (Array.isArray(message)) {
                this.errorMsg = message.join(', ');
            } else {
                this.errorMsg = message;
            }
            this.error = true;
        },
        objectToArray: function(content) {
            // used to take returned error objects and output them to array of strings
            let outputArray = [];
            if (Object.keys(content).length) {  // check if content has keys (is an object, really)
                for (const [key, value] of Object.entries(content)) {
                    outputArray.push(`${key}: ${value}`);
                }
            } else if (Array.isArray(content)) {
                outputArray = [...content];
            } else {
                outputArray = [content];
            }
            return outputArray;
        },
        getSetupData: function() {
            // fetch data needed for select boxes, and other 'setup' type data
            // combined eveything you need into one endpoint and make sure all select_related's are working so its fast
            this.loadingSetupData = true;
            suppliosApi.rfx.getRfxSetupOptions()
            .then(response => {
                console.log('Got setupData:');
                console.log(response);
                this.setupData = Object.assign({}, response.data);
            })
            .catch(error => this.handleError(error, 'Error while getting data for form'))
            .finally(() => this.loadingSetupData = false)
        },
        formatDataForSave: function() {
            // grabs current data from this.itemData (cloneDeep), picks off only the fields you want to send to API in the correct format / field names
            let fields_to_save = ['title', 'rfx_type', 'sourcing_category'];
            let snapshotData = cloneDeep(this.itemData);
            let returnObj = pick(snapshotData, fields_to_save);
            // now manually force 'sourcing_category' to null if its empty... instead of empty string, which may throw-off backend
            if (!returnObj.sourcing_category) {  // check for any falsey value, such as empty string
                returnObj.sourcing_category = null;
            }
            return returnObj;
        },
        resetRefsValid: function() {
            // resets all refs you checked to default validation state so things aren't always in red outline if validation problems
            let allRefs = Object.values(this.$refs);
            allRefs.forEach(ref => {
                if (ref && Object.keys(ref).includes('resetValidity')) {  // have to check if refs are actually present and if they have resetValidity method
                    ref.resetValidity();  // do the reset
                }
            });
        },
        checkRefsValid: function() {
            let allRefs = Object.values(this.$refs);  // this.$refs is an object, Object.values() makes an array of actual refs
            let refsInvalidList = [];  // we'll make this below... a list of errors from checking refs
            let allRefsValid = true;  // set it to true here, then set to false if you find any invalid in loop below
            allRefs.forEach(ref => {
                if (ref) {  // have to check if refs are actually present
                    let refKeys = Object.keys(ref);
                    if (refKeys.includes('checkValidity')) {
                        let isRefValid = ref.checkValidity();
                        if (isRefValid === false) {  // need to specifically check if 'false' -- because some hot-edit-inputs return 'null' if they aren't in edit mode, we'll ignore those
                            refsInvalidList.push(ref.checkInvalidMessage());
                            allRefsValid = false;
                        }
                    }
                }
            });
            this.refsInvalidList = refsInvalidList;
            this.allRefsValid = allRefsValid;
            return allRefsValid;  // returns a Boolean
        },
        saveNewItem: function() {  // CAN BE CALLED FROM PARENT - for adding new thing, not updating existing
            if (!this.checkRefsValid()) return Promise.resolve(false);
            this.loading = true;
            let saveObject = this.formatDataForSave();
            const savePromise = suppliosApi.rfx.create(saveObject)
                .then(response => {
                    console.log('successful create, response below');
                    console.log(response);
                    return response.data;
                })
                .then(createdRfxObj => {
                    return this.handleAddComplete(createdRfxObj);  // this method will do a redirect before the promise resolves...
                })
                .catch(error => {  // if you have error, keep the form open and in edit mode, showing errors, allowing user to correct things
                    this.handleError(error, this.objectToArray(error.response.data));  // error will likely be an object with key = field name and value = error message
                    return false;
                })
                .then(status => {
                    this.loading = false;
                    return status
                });
            return savePromise;
        },
        // below were from wrapper modal 
        handleAddComplete: function(newObject) {
            // newObject should be the newly created object returned from API (in response.data)
            console.log('From parent component, we got this newObject passed up from save-complete event');
            console.log(newObject);
            this.showSuccess = true;
            setTimeout(() => {  // AFTER the timeout, do the redirect if we have URL
                let redirectUrl = newObject.internal_setup_url;
                if (redirectUrl) {
                    window.location.href = redirectUrl;  // THE REDIRECT
                } else {
                    this.modalShowing = false;
                }
            }, this.successMessageTime);
            return true;
        },
        resetInputs: function() {
            this.error = false;
            this.resetRefsValid(); // set all refs to their default validation visible state
            this.allRefsValid = true;
            this.refsInvalidList = [];
            this.itemData = Object.assign({}, this.blankItemData);
        },
        onModalHidden: function() {
            this.resetInputs();
            this.showSuccess = false;
        },
        onModalShow: function() {
            // all this stuff would normally go in mounted hook, but we moved here so it loads only when modal shows
            // set initial data object (itemData) with 'hard-coded' newData if provided
            if (!isEmpty(this.newData)) {
                this.itemData = Object.assign({}, this.itemData, this.newData);
            } else {
                this.itemData = Object.assign({}, this.blankItemData);
            }
            // then get options for select boxes on form
            this.getSetupData();
        }
    }
}
</script>

<style>

</style>